import { unwrapResult } from '@reduxjs/toolkit'
import {
  applePaySelector,
  ApplePayState,
  AsyncAction,
  checkoutDataSelector,
  getShippingAddresses,
  preferredCardSelector,
  preferredBankAccountSelector,
  checkoutSelector,
  setPickupAddress,
  cleanUpConsumerCredentials,
  consumerSelector,
  startCheckout,
  profilingIdSelector,
  getCashCheckoutToken,
} from '../..'
import { getDeviceDetails } from '../../../utils'
import { PreferredCard } from '../../../utils/credit-card'
import { PreferredBankAccount } from '../../../utils/bank-account'
import { canUseApplePay } from '../../../utils/browser'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { getSessionStorageItem } from '@afterpay/utils/storage'
import { PromoExclusion, CheckoutMode, Cohort } from '@afterpaytouch/portal-api/types'
import { Theme } from '@afterpaytouch/core'
import { getCheckoutApiVersionForOrderSource } from '../apiVersion'

export const startCheckoutAndSelectPreferredPaymentMethod =
  (
    isEnabledForMPANApplePayFlow: boolean = false,
    checkoutTheme: Theme,
    checkoutCohort?: Cohort
  ): AsyncAction<{
    preferredCard: PreferredCard | undefined
    applePay: ApplePayState
    preferredBankAccount: PreferredBankAccount | undefined
  }> =>
  async (dispatch, getState) => {
    const { checkoutMode, shippingAddress } = checkoutDataSelector(getState()) ?? {}
    const { initial } = checkoutSelector(getState())
    const { email, mobile } = consumerSelector(getState())
    const orderSource = checkoutDataSelector(getState())?.merchant?.source
    const startCheckoutApiVersion = getCheckoutApiVersionForOrderSource(orderSource)

    // set to checkout shipping address when pickup
    if (checkoutMode === CheckoutMode.EXPRESS && initial.pickup && shippingAddress !== undefined && shippingAddress !== null) {
      dispatch(setPickupAddress(shippingAddress))
    }

    const promoExclusion = await getSessionStorageItem<PromoExclusion>('promoExclusion')
    const cashCheckoutToken = await dispatch(getCashCheckoutToken()).unwrap()

    const results = await Promise.allSettled([
      dispatch(
        startCheckout.initiate(
          {
            token: initial.token,
            ...(startCheckoutApiVersion === 'v2' && { apiVersion: startCheckoutApiVersion }),
            body: {
              deviceDetails: getDeviceDetails(),
              profilingSessionId: profilingIdSelector(getState()),
              ...(promoExclusion != null && { promoExclusion }),
              checkoutTheme,
              ...(checkoutCohort && { checkoutCohort }),
            },
            headers: cashCheckoutToken != null ? { 'x-cash-token': cashCheckoutToken } : undefined,
          },
          { forceRefetch: true }
        )
      ),
      dispatch(canUseApplePay(isEnabledForMPANApplePayFlow)),
      dispatch(cleanUpConsumerCredentials(email, mobile)),
    ])

    // These additional api calls depend on start to finish first
    const additionalResults = await Promise.allSettled([checkoutMode === CheckoutMode.EXPRESS && !initial.pickup && dispatch(getShippingAddresses())])

    handleStartCheckoutResults([...results, ...additionalResults])

    return {
      preferredCard: preferredCardSelector(getState()),
      applePay: applePaySelector(getState()),
      preferredBankAccount: preferredBankAccountSelector(getState()),
    }
  }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleStartCheckoutResults = (results: Array<PromiseSettledResult<any>>): void => {
  const [startCheckoutRes] = results

  if (startCheckoutRes.status === 'fulfilled') {
    unwrapResult(startCheckoutRes.value)
  }

  results.forEach((result) => {
    if (result.status === 'rejected') {
      capture.error(result.reason)
    }
  })
}
